import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Trophy, Warning, VideoCamera, UsersThree, CurrencyDollar, Calendar, Sparkle,
  ArrowUpRight, LockKey, LockKeyOpen, ChalkboardTeacher, Copy, ArrowsClockwise, Compass, Bell, Check,
} from "@phosphor-icons/react";

const BRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) : "—";
const fmtDateTime = (iso) => iso
  ? new Date(iso).toLocaleString("pt-BR", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
  : "—";

const medal = (i) => ["🥇", "🥈", "🥉"][i] || `${i + 1}º`;

const scoreColor = (score) => {
  if (score == null) return "bg-zinc-100 text-zinc-500";
  if (score >= 80) return "bg-[#EAF7EF] text-[#12B76A]";
  if (score >= 60) return "bg-[#E0EBFF] text-[#1E5FFF]";
  if (score >= 40) return "bg-[#FFF6EA] text-[#F79009]";
  return "bg-[#FFE9EE] text-[#FF3D71]";
};

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map(s => s[0]).join("").toUpperCase();
}

export default function TeacherDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [pending, setPending] = useState([]);
  const [anamMap, setAnamMap] = useState({}); // student_id -> count
  const [notifications, setNotifications] = useState([]);

  async function load() {
    setLoading(true);
    try {
      const [r, inv, students, anam, notif] = await Promise.all([
        api.get("/teacher/dashboard"),
        api.get("/teacher/invite-code").catch(() => ({ data: null })),
        api.get("/students").catch(() => ({ data: [] })),
        api.get("/anamnesis").catch(() => ({ data: [] })),
        api.get("/teacher/notifications", { params: { unread_only: true } }).catch(() => ({ data: [] })),
      ]);
      setData(r.data);
      setInvite(inv.data);
      setPending((students.data || []).filter(s => s.access_unlocked === false));
      const m = {};
      (anam.data || []).forEach(a => { m[a.student_id] = (m[a.student_id] || 0) + 1; });
      setAnamMap(m);
      setNotifications(notif.data || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function unlockStudent(sid, name) {
    if (!window.confirm(`Liberar acesso completo à plataforma para ${name}?\n\nSe houver anamnese respondida, um plano de estudos personalizado será gerado automaticamente e ${name.split(" ")[0]} receberá um e-mail.`)) return;
    try {
      const r = await api.post(`/students/${sid}/unlock-access`);
      const parts = [`${name} liberado(a)`];
      if (r.data?.auto_plan) parts.push("plano gerado");
      if (r.data?.email_sent) parts.push("e-mail enviado");
      toast.success(parts.join(" — "));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao liberar"); }
  }

  async function dismissNotif(nid) {
    try { await api.post(`/teacher/notifications/${nid}/read`); load(); }
    catch (e) { console.warn("mark read failed", e); }
  }

  async function dismissAll() {
    try {
      await api.post("/teacher/notifications/read-all");
      toast.success("Notificações marcadas como lidas");
      load();
    } catch (e) { toast.error("Erro"); }
  }

  async function copyInvite() {
    if (!invite?.invite_url) return;
    try {
      await navigator.clipboard.writeText(invite.invite_url);
      toast.success("Link de convite copiado!");
    } catch { prompt("Copie o link manualmente:", invite.invite_url); }
  }
  async function regenerateInvite() {
    if (!confirm("Gerar um novo código? O link atual deixará de funcionar.")) return;
    try {
      const r = await api.post("/teacher/invite-code/regenerate");
      setInvite(r.data);
      toast.success("Novo código gerado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  }

  const stats = [
    { icon: UsersThree, tint: "#1E5FFF", bg: "gradient-sky", label: "Meus alunos", value: data?.total_students ?? 0 },
    { icon: Trophy, tint: "#7A5CFF", bg: "bg-[#F3EEFF]", label: "No ranking", value: data?.ranking?.filter(r => r.avg_score != null).length ?? 0 },
    { icon: Warning, tint: "#FF3D71", bg: "gradient-blush", label: "Inadimplentes", value: data?.inadimplentes?.length ?? 0 },
    { icon: VideoCamera, tint: "#12B76A", bg: "bg-[#EAF7EF]", label: "Sessões (7 dias)", value: data?.weekly_sessions?.length ?? 0 },
  ];

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-7xl space-y-6 sm:space-y-8">
      <header className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="pill-badge bg-white text-[#1E5FFF] border border-[#1E5FFF]/20 mb-4">
            <ChalkboardTeacher size={12} weight="fill" /> Painel do Professor
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-[#12143A] leading-tight">
            Olá, {user?.name?.split(" ")[0]} <Sparkle size={28} className="inline text-[#F79009]" weight="fill" />
          </h1>
          <p className="text-zinc-500 mt-2 text-sm sm:text-base">
            Aqui está o pulso dos seus alunos essa semana.
          </p>
        </div>
        <Link to="/students-manage" data-testid="go-students-manage"
              className="btn-pill h-12 px-6 rounded-full gradient-primary text-white font-bold shadow-[0_8px_24px_rgba(30,95,255,0.35)] hover:-translate-y-0.5 transition-transform flex items-center gap-2">
          Gerenciar alunos <ArrowUpRight size={16} weight="bold" />
        </Link>
      </header>

      {invite?.invite_url && (
        <div data-testid="invite-card" className="card-soft p-5 sm:p-6 fade-up flex items-center gap-4 flex-wrap bg-gradient-to-r from-[#E0EBFF] to-[#F3EEFF]">
          <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center shadow-sm">
            <ChalkboardTeacher size={22} weight="duotone" color="#1E5FFF" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#1E5FFF]">Seu link de convite</div>
            <div className="font-mono text-sm text-[#12143A] break-all">{invite.invite_url}</div>
            <div className="text-xs text-zinc-500 mt-0.5">Compartilhe com alunos e responsáveis. Só quem tem esse link consegue se cadastrar sob sua tutoria.</div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="copy-invite" onClick={copyInvite}
                    className="h-11 px-4 rounded-full bg-white text-[#1E5FFF] font-bold text-sm hover:bg-[#1E5FFF] hover:text-white transition-colors flex items-center gap-2 shadow-sm">
              <Copy size={16} weight="bold" /> Copiar Link de Convite
            </button>
            <button data-testid="regen-invite" onClick={regenerateInvite}
                    title="Gerar novo código (invalida o atual)"
                    className="h-11 w-11 rounded-full bg-white text-zinc-500 hover:text-[#F79009] flex items-center justify-center shadow-sm">
              <ArrowsClockwise size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {notifications.length > 0 && (
        <div data-testid="notif-card" className="card-soft overflow-hidden fade-up border-l-4 border-[#1E5FFF]" style={{ animationDelay: '0.04s' }}>
          <div className="p-5 sm:p-6 border-b border-zinc-100 flex items-center gap-3 bg-gradient-to-r from-[#E0EBFF] to-white">
            <div className="w-11 h-11 rounded-2xl bg-white border border-[#1E5FFF]/30 flex items-center justify-center shadow-sm flex-shrink-0 animate-pulse-slow">
              <Bell size={20} weight="fill" color="#1E5FFF" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-lg sm:text-xl font-black">Novas anamneses concluídas</h3>
              <p className="text-xs text-zinc-500">Alunos que acabaram de responder o perfil de estudos.</p>
            </div>
            <span className="pill-badge bg-[#1E5FFF] text-white font-mono-num">{notifications.length}</span>
            <button data-testid="notif-mark-all" onClick={dismissAll}
                    className="hidden sm:flex text-[10px] uppercase tracking-widest font-bold text-zinc-500 hover:text-[#1E5FFF] items-center gap-1">
              <Check size={12} weight="bold" /> Marcar todas
            </button>
          </div>
          <div className="divide-y divide-zinc-50 max-h-[320px] overflow-y-auto">
            {notifications.map(n => (
              <div key={n.id} data-testid={`notif-${n.id}`} className="p-4 sm:p-5 flex items-center gap-3 hover:bg-zinc-50/50">
                <div className="w-10 h-10 rounded-2xl bg-[#F4F8FF] flex items-center justify-center flex-shrink-0">
                  <Compass size={18} weight="duotone" color="#1E5FFF" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-[#12143A] truncate">{n.student_name}</div>
                  <div className="text-xs text-zinc-500 truncate">
                    Perfil: <b>{n.perfil_principal || "—"}</b> • Foco: {n.principal_foco || "—"}
                  </div>
                </div>
                <button data-testid={`notif-unlock-${n.id}`} onClick={() => unlockStudent(n.student_id, n.student_name)}
                        className="h-9 px-3 rounded-full gradient-primary text-white font-bold text-xs hover:-translate-y-0.5 transition-transform flex items-center gap-1 flex-shrink-0">
                  <LockKeyOpen size={12} weight="fill" /> Liberar
                </button>
                <button data-testid={`notif-dismiss-${n.id}`} onClick={() => dismissNotif(n.id)}
                        title="Marcar como lida"
                        className="w-9 h-9 rounded-full bg-white hover:bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-400 hover:text-[#12B76A] flex-shrink-0">
                  <Check size={14} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {pending.length > 0 && (
        <div data-testid="pending-unlock-card" className="card-soft overflow-hidden fade-up" style={{ animationDelay: '0.08s' }}>
          <div className="p-5 sm:p-6 border-b border-zinc-100 flex items-center gap-3 bg-gradient-to-r from-[#FFF6EA] to-white">
            <div className="w-11 h-11 rounded-2xl bg-white border border-[#F79009]/30 flex items-center justify-center shadow-sm flex-shrink-0">
              <LockKey size={20} weight="duotone" color="#B45309" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-lg sm:text-xl font-black">Aguardando liberação</h3>
              <p className="text-xs text-zinc-500">Alunos que se cadastraram por convite e ainda só têm acesso à anamnese.</p>
            </div>
            <span className="pill-badge bg-[#FFF6EA] text-[#B45309] font-mono-num">{pending.length}</span>
          </div>
          <div className="divide-y divide-zinc-50 max-h-[380px] overflow-y-auto">
            {pending.map(s => {
              const done = (anamMap[s.id] || 0) > 0;
              return (
                <div key={s.id} data-testid={`pending-${s.id}`} className="p-4 sm:p-5 flex items-center gap-3 hover:bg-zinc-50/50">
                  <div className="w-10 h-10 rounded-full gradient-warm flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-heading font-black text-xs">{initials(s.name)}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-[#12143A] truncate">{s.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{s.email}</div>
                  </div>
                  <span className={`pill-badge text-[10px] uppercase tracking-widest font-bold ${done ? "bg-[#DCF7E5] text-[#12B76A]" : "bg-zinc-100 text-zinc-500"}`}>
                    <Compass size={10} weight="fill" /> {done ? "Anamnese ok" : "Sem anamnese"}
                  </span>
                  <button data-testid={`unlock-${s.id}`} onClick={() => unlockStudent(s.id, s.name)}
                          className="h-9 px-3 rounded-full gradient-primary text-white font-bold text-xs hover:-translate-y-0.5 transition-transform flex items-center gap-1 flex-shrink-0">
                    <LockKeyOpen size={12} weight="fill" /> Liberar
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className="card-soft p-16 text-center text-zinc-400 fade-up">Carregando…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 fade-up" style={{ animationDelay: '0.05s' }}>
            {stats.map(({ icon: Icon, tint, bg, label, value }) => (
              <div key={label} className="card-soft p-5 relative overflow-hidden">
                <div className={`absolute inset-0 opacity-50 ${bg}`} />
                <div className="relative">
                  <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center shadow-sm mb-3">
                    <Icon size={18} weight="duotone" color={tint} />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{label}</div>
                  <div className="font-mono-num text-3xl font-black text-[#12143A] mt-1">{value}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Ranking */}
            <div className="lg:col-span-2 card-soft overflow-hidden fade-up" style={{ animationDelay: '0.1s' }} data-testid="ranking-card">
              <div className="p-5 sm:p-6 border-b border-zinc-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-[#F3EEFF] flex items-center justify-center">
                  <Trophy size={20} weight="duotone" color="#7A5CFF" />
                </div>
                <div>
                  <h3 className="font-heading text-lg sm:text-xl font-black">Ranking dos alunos</h3>
                  <p className="text-xs text-zinc-500">Média ponderada de todas as avaliações</p>
                </div>
              </div>
              <div className="divide-y divide-zinc-50">
                {(data?.ranking || []).slice(0, 10).map((r, i) => (
                  <div key={r.student_id} data-testid={`rank-${r.student_id}`}
                       className="p-4 sm:p-5 flex items-center gap-3 hover:bg-zinc-50/50 transition-colors">
                    <div className="w-10 flex-shrink-0 text-center font-heading font-black text-lg text-[#12143A]">
                      {r.avg_score != null ? medal(i) : "—"}
                    </div>
                    <div className="w-10 h-10 rounded-full gradient-warm flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-heading font-black text-xs">{initials(r.name)}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-[#12143A] truncate">{r.name}</div>
                      <div className="text-xs text-zinc-500">
                        {r.evaluations > 0 ? `${r.evaluations} avaliaç${r.evaluations > 1 ? "ões" : "ão"} • última em ${fmtDate(r.last_date)}` : "Sem avaliações ainda"}
                      </div>
                    </div>
                    <div className={`pill-badge font-mono-num ${scoreColor(r.avg_score)}`}>
                      {r.avg_score != null ? `${r.avg_score}%` : "—"}
                    </div>
                  </div>
                ))}
                {(!data?.ranking || data.ranking.length === 0) && (
                  <div className="p-16 text-center text-zinc-400">
                    <Trophy size={40} weight="duotone" className="mx-auto mb-3 text-zinc-300" />
                    Nenhum aluno atribuído ainda.
                  </div>
                )}
              </div>
            </div>

            {/* Inadimplência + Sessões */}
            <div className="space-y-6">
              <div className="card-soft overflow-hidden fade-up" style={{ animationDelay: '0.15s' }} data-testid="inadimplentes-card">
                <div className="p-5 border-b border-zinc-100 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-[#FFE9EE] flex items-center justify-center">
                    <Warning size={20} weight="duotone" color="#FF3D71" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-black">Alertas financeiros</h3>
                    <p className="text-xs text-zinc-500">Inadimplência e pagamentos vencidos</p>
                  </div>
                </div>
                <div className="divide-y divide-zinc-50 max-h-[380px] overflow-y-auto">
                  {(data?.inadimplentes || []).map(i => (
                    <div key={i.student_id} data-testid={`inad-${i.student_id}`} className="p-4 hover:bg-zinc-50/50">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-full gradient-warm flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-heading font-black text-xs">{initials(i.name)}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-[#12143A] text-sm truncate">{i.name}</div>
                          <div className="text-xs text-zinc-500 truncate">{i.email}</div>
                        </div>
                        {!i.active && (
                          <span className="pill-badge bg-zinc-100 text-zinc-500 text-[10px]" title="Acesso bloqueado">
                            <LockKey size={10} weight="fill" />
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-zinc-500">
                          {i.overdue_count > 0
                            ? `${i.overdue_count} venc${i.overdue_count > 1 ? "idas" : "ida"} • desde ${fmtDate(i.oldest_due)}`
                            : "Marcado inadimplente"}
                        </span>
                        <span className="font-mono-num font-black text-[#FF3D71]">{BRL(i.overdue_total)}</span>
                      </div>
                    </div>
                  ))}
                  {(!data?.inadimplentes || data.inadimplentes.length === 0) && (
                    <div className="p-10 text-center text-zinc-400 text-sm">
                      <div className="text-3xl mb-2">🎉</div>
                      Todos em dia!
                    </div>
                  )}
                </div>
                <div className="p-3 border-t border-zinc-100">
                  <Link to="/payments" data-testid="go-payments"
                        className="w-full block text-center text-xs font-bold text-[#1E5FFF] hover:underline py-2">
                    Ir para Mensalidades →
                  </Link>
                </div>
              </div>

              <div className="card-soft overflow-hidden fade-up" style={{ animationDelay: '0.2s' }} data-testid="weekly-sessions-card">
                <div className="p-5 border-b border-zinc-100 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-[#EAF7EF] flex items-center justify-center">
                    <VideoCamera size={20} weight="duotone" color="#12B76A" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-black">Sessões da semana</h3>
                    <p className="text-xs text-zinc-500">Próximos 7 dias</p>
                  </div>
                </div>
                <div className="divide-y divide-zinc-50 max-h-[320px] overflow-y-auto">
                  {(data?.weekly_sessions || []).map(s => (
                    <div key={s.id} data-testid={`sess-${s.id}`} className="p-4 hover:bg-zinc-50/50">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#EAF7EF] flex items-center justify-center flex-shrink-0">
                          <Calendar size={16} weight="duotone" color="#12B76A" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-[#12143A] text-sm truncate">{s.title}</div>
                          <div className="text-xs text-zinc-500 truncate">{s.student_name}</div>
                          <div className="text-xs text-zinc-400 mt-1">{fmtDateTime(s.start)} • {s.duration_min}min</div>
                          {s.meet_link && (
                            <a href={s.meet_link} target="_blank" rel="noreferrer"
                               className="inline-block mt-2 text-xs font-bold text-[#1E5FFF] hover:underline">
                              Entrar no Meet →
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!data?.weekly_sessions || data.weekly_sessions.length === 0) && (
                    <div className="p-10 text-center text-zinc-400 text-sm">
                      <VideoCamera size={32} weight="duotone" className="mx-auto mb-2 text-zinc-300" />
                      Nenhuma sessão agendada.
                    </div>
                  )}
                </div>
                <div className="p-3 border-t border-zinc-100">
                  <Link to="/tutoring" data-testid="go-tutoring"
                        className="w-full block text-center text-xs font-bold text-[#1E5FFF] hover:underline py-2">
                    Agendar nova sessão →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
